import { useSyncExternalStore } from 'react';
import { Linking, Platform } from 'react-native';
import { MOD_IDS, type Anexo, type DocId } from '../store/types';
import { allDocs, getDoc, replaceAll, setDoc, syncNow } from '../store/store';
import { apagarAnexo, escolherArquivo, escolherVarios, lerBase64, soNesteAparelho } from './anexos';
import { semBloqueio } from '../auth/AuthContext';
import { supabase } from './supabase';
import { t } from './i18n';
import { DEMO } from './env';
import { paraB64 } from './cofre';

// Google Drive e OneDrive do usuário, pela função "nuvem" do servidor (supabase/functions/nuvem).
// O app nunca vê as senhas nem as chaves de acesso do Google/Microsoft: a função guarda e usa.

export type Provedor = 'google' | 'microsoft';
export type ContaNuvem = { provedor: Provedor; email: string | null; conectado_em: string; ultimo_backup: string | null; auto_backup: boolean };
export type Copia = { id: string; nome: string; criado: string; tamanho: number };
type Estado = { carregado: boolean; erro: string | null; configurado: Record<Provedor, boolean>; contas: ContaNuvem[] };

export const NOME: Record<Provedor, string> = { google: 'Google Drive', microsoft: 'OneDrive' };

let estado: Estado = { carregado: false, erro: null, configurado: { google: false, microsoft: false }, contas: [] };
const ouvintes = new Set<() => void>();
const set = (p: Partial<Estado>) => { estado = { ...estado, ...p }; ouvintes.forEach(f => f()); };
export const useNuvem = () => useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => estado);
/** Estado atual (para o painel de estado do servidor). */
export const estadoNuvem = () => estado;
export const contaDe = (p: Provedor) => estado.contas.find(c => c.provedor === p) ?? null;

/** Chama a função "nuvem" e devolve a resposta (ou lança o erro já em português). */
// Prévia de demonstração (sem servidor): respostas de exemplo.
let demoContas: ContaNuvem[] = [{ provedor: 'google', email: 'walter@gmail.com', conectado_em: new Date().toISOString(), ultimo_backup: null, auto_backup: true }];
async function demo(acao: string, extra: Record<string, unknown>): Promise<any> {
  const p = extra.provedor as Provedor;
  switch (acao) {
    case 'status': return { configurado: { google: true, microsoft: true }, contas: demoContas };
    case 'iniciar': demoContas = [...demoContas.filter(c => c.provedor !== p), { provedor: p, email: 'walter@outlook.com', conectado_em: new Date().toISOString(), ultimo_backup: null, auto_backup: true }]; return { url: '/?nuvem=ok&provedor=' + p };
    case 'desconectar': demoContas = demoContas.filter(c => c.provedor !== p); return { ok: true };
    case 'auto': demoContas = demoContas.map(c => (c.provedor === p ? { ...c, auto_backup: !!extra.ligado } : c)); return { ok: true };
    case 'copia': demoContas = demoContas.map(c => (c.provedor === p ? { ...c, ultimo_backup: new Date().toISOString() } : c)); return { nome: 'MyPlanner-copia-demo.json', url: '' };
    case 'copias': return { copias: [{ id: 'c1', nome: 'MyPlanner-copia-2026-10-04-09-00.json', criado: '2026-10-04T09:00:00Z', tamanho: 48213 }] };
    case 'enviar': return { id: 'x' + Date.now(), nome: String(extra.nome), url: 'https://drive.google.com/file/d/demo/view' };
    default: throw new Error('demo');
  }
}

async function chamar<T = any>(acao: string, extra: Record<string, unknown> = {}): Promise<T> {
  if (DEMO) return demo(acao, extra);
  if (!supabase) throw new Error(t('Servidor não configurado.'));
  const { data, error } = await supabase.functions.invoke('nuvem', { body: { acao, ...extra } });
  if (error) {
    const resp: Response | undefined = (error as any).context;
    const status = resp?.status ?? 0;
    let corpo: any = null;
    try { corpo = await resp?.clone().json(); } catch { try { corpo = { erro: await resp?.text() }; } catch { /* sem corpo */ } }
    let msg = typeof corpo?.erro === 'string' ? corpo.erro : '';
    if (status === 404 && !msg) msg = t('A função "nuvem" ainda não foi publicada no servidor. Rode o 32-LIGAR-DRIVE-ONEDRIVE.bat.');
    else if (status === 401 && !msg) msg = t('O servidor recusou o acesso: na função "nuvem" do Supabase, desligue "Verify JWT" (o 32-LIGAR-DRIVE-ONEDRIVE.bat já faz isso).');
    else if (!msg && /Failed to send|network/i.test(String(error.message))) msg = t('Sem conexão com o servidor. Confira a internet.');
    throw new Error(msg || `${String(error.message)}${status ? ` (${status})` : ''}`);
  }
  return data as T;
}

export async function atualizarNuvem() {
  try {
    const r = await chamar<{ configurado: Record<Provedor, boolean>; contas: ContaNuvem[] }>('status');
    set({ carregado: true, erro: null, configurado: r.configurado, contas: r.contas });
  } catch (e: any) { set({ carregado: true, erro: String(e?.message ?? e) }); }
}

/** Abre a página do Google/Microsoft para autorizar. Ao voltar para o app, a tela atualiza sozinha. */
export async function conectar(p: Provedor) {
  const { url } = await chamar<{ url: string }>('iniciar', { provedor: p });
  if (Platform.OS === 'web') { window.open(url, 'myplanner-nuvem', 'width=520,height=720'); return; }
  await semBloqueio(() => Linking.openURL(url));
}

export async function desconectar(p: Provedor) {
  await chamar('desconectar', { provedor: p });
  if (getDoc('ajustes').nuvem?.destino === p) setDoc('ajustes', d => ({ ...d, nuvem: { destino: null } }));
  await atualizarNuvem();
}

export async function copiaAutomatica(p: Provedor, ligado: boolean) {
  await chamar('auto', { provedor: p, ligado });
  await atualizarNuvem();
}

/** Cópia completa agora (envia antes o que ainda não subiu para a conta). */
export async function fazerCopia(p: Provedor) {
  await syncNow().catch(() => {});
  const r = await chamar<{ nome: string; url: string }>('copia', { provedor: p });
  await atualizarNuvem();
  return r;
}
export const listarCopias = (p: Provedor) => chamar<{ copias: Copia[] }>('copias', { provedor: p }).then(r => r.copias);

/** Troca todos os dados pelos da cópia escolhida (aqui e na conta). */
export async function restaurarCopia(p: Provedor, id: string) {
  const json = await chamar<any>('baixar_copia', { provedor: p, id });
  if (json?.app !== 'myplanner' || typeof json.dados !== 'object') throw new Error(t('Este arquivo não é um backup do My Planner.'));
  replaceAll(json.dados);
  await syncNow().catch(() => {});
}

/** Guarda um anexo no Drive/OneDrive. Mantém a cópia do aparelho (abre mais rápido nele) e ganha o link. */
export async function guardarNaNuvem(a: Anexo, p: Provedor): Promise<Anexo> {
  const base64 = await lerBase64(a);
  const r = await chamar<{ id: string; nome: string; url: string }>('enviar', { provedor: p, nome: a.nome, mime: a.mime, base64 });
  const pequenaImagem = !!a.dado && !!a.mime?.startsWith('image/') && (a.tamanho ?? 0) <= 200 * 1024;
  return { ...a, url: r.url, nuvem: { p, id: r.id }, dado: pequenaImagem ? a.dado : undefined };
}

/** iPhone (0.53): depois que o arquivo foi para o Drive/OneDrive do usuário, o app fica só com o link
 *  (abre no app do Drive/OneDrive) e apaga a cópia dele. Imagens continuam aqui para aparecer na tela. */
export function semCopiaLocal(a: Anexo): Anexo {
  if (Platform.OS !== 'ios' || !a.url || !a.uri || a.mime?.startsWith('image/')) return a;
  apagarAnexo(a);
  return { ...a, uri: undefined, aparelho: undefined };
}

/** Destino escolhido para os anexos novos (só se a conta continua conectada). */
export function destinoAtual(): Provedor | null {
  const d = getDoc('ajustes').nuvem?.destino ?? null;
  return d && contaDe(d) ? d : null;
}

/** Escolhe um arquivo (no celular, o seletor mostra também Drive, OneDrive e Dropbox) e, se houver destino
 *  na nuvem, guarda lá. Se a nuvem falhar, o arquivo fica no aparelho e o motivo vem em "aviso". */
export async function escolherEGuardar(imagem = false, semNuvem = false): Promise<{ arq: Anexo; aviso?: string } | null> {
  const destino = semNuvem ? null : destinoAtual();
  const a = await semBloqueio(() => escolherArquivo(imagem, !!destino));
  if (!a) return null;
  // 0.53: atalho para o arquivo original (Android) não é copiado nem enviado para lugar nenhum
  if (!destino || a.atalho) return { arq: a };
  try { return { arq: semCopiaLocal(await guardarNaNuvem(a, destino)) }; }
  catch (e: any) {
    if (Platform.OS === 'web') throw e;   // no navegador, arquivo grande sem nuvem não tem onde ficar
    return { arq: a, aviso: t('O arquivo ficou só neste dispositivo. Não foi possível guardar no {nome}: {erro}', { nome: NOME[destino], erro: String(e?.message ?? e) }) };
  }
}

/** Várias fotos e vídeos de uma vez; cada um vai para a nuvem se houver destino. */
export async function escolherVariosEGuardar(semNuvem = false): Promise<{ arqs: Anexo[]; aviso?: string }> {
  const lista = await semBloqueio(() => escolherVarios());
  const arqs: Anexo[] = [];
  let aviso: string | undefined;
  for (const a of lista) { const r = await guardarSeTiverNuvem(a, semNuvem); arqs.push(r.arq); aviso ??= r.aviso; }
  return { arqs, aviso };
}

/** Depois de tirar uma foto: guarda também na nuvem, se houver destino. */
export async function guardarSeTiverNuvem(a: Anexo, semNuvem = false): Promise<{ arq: Anexo; aviso?: string }> {
  const destino = semNuvem ? null : destinoAtual();
  if (!destino || a.atalho) return { arq: a };
  try { return { arq: await guardarNaNuvem(a, destino) }; }
  catch (e: any) { return { arq: a, aviso: t('O arquivo ficou só neste dispositivo. Não foi possível guardar no {nome}: {erro}', { nome: NOME[destino], erro: String(e?.message ?? e) }) }; }
}

// ─── Enviar para a nuvem os arquivos que estão só neste aparelho ────────────────────────────────
const ABAS_COM_ANEXOS: DocId[] = ['estudos', 'documentos', 'planner', 'garantias', ...MOD_IDS];
const ehAnexo = (x: any): x is Anexo => x && typeof x === 'object' && typeof x.nome === 'string' && ('uri' in x || 'dado' in x || 'url' in x);

function coletar(x: any, out: Anexo[]) {
  if (Array.isArray(x)) x.forEach(y => coletar(y, out));
  else if (x && typeof x === 'object') { if (ehAnexo(x) && soNesteAparelho(x)) out.push(x); else Object.values(x).forEach(y => coletar(y, out)); }
}
function trocar(x: any, mapa: Map<Anexo, Anexo>): any {
  if (Array.isArray(x)) return x.map(y => trocar(y, mapa));
  if (x && typeof x === 'object') {
    if (mapa.has(x)) return mapa.get(x);
    const r: any = {};
    for (const [k, v] of Object.entries(x)) r[k] = trocar(v, mapa);
    return r;
  }
  return x;
}
/** Quantos arquivos estão só neste aparelho. */
export function pendentesNoAparelho() {
  const out: Anexo[] = [];
  for (const id of ABAS_COM_ANEXOS) coletar(allDocs()[id], out);
  return out.length;
}
/** Envia todos para o destino. Devolve quantos foram e quantos falharam. */
export async function enviarPendentes(p: Provedor, progresso?: (feitos: number, total: number) => void) {
  let ok = 0, falhas = 0;
  const total = pendentesNoAparelho();
  for (const id of ABAS_COM_ANEXOS) {
    const lista: Anexo[] = [];
    coletar(allDocs()[id], lista);
    const mapa = new Map<Anexo, Anexo>();
    for (const a of lista) {
      try { mapa.set(a, await guardarNaNuvem(a, p)); ok++; } catch { falhas++; }
      progresso?.(ok + falhas, total);
    }
    // troca pelo objeto atual do documento (se algo mudou no meio do envio, o que não foi enviado fica como estava)
    if (mapa.size) setDoc(id, d => trocar(d, mapa));
  }
  return { ok, falhas };
}

/** Guarda um arquivo gerado no app (ex.: PDF do Scanner) no Drive/OneDrive. Até 15 MB. */
export async function enviarBytes(p: Provedor, nome: string, mime: string, bytes: Uint8Array): Promise<{ id: string; url: string }> {
  if (bytes.length > 15 * 1024 * 1024) throw new Error(t('O arquivo passa de 15 MB. Ligue "Arquivo menor" e tente de novo.'));
  const r = await chamar<{ id: string; nome: string; url: string }>('enviar', { provedor: p, nome, mime, base64: paraB64(bytes) });
  return { id: r.id, url: r.url };
}
